import { useQuery, UseQueryResult } from '@tanstack/react-query';
import deploymentService from '../../../services/DeploymentService';
import { DeploymentResult } from '../../../models/Deployment';

const POLLING_INTERVAL_MS = 30_000;
const DEFAULT_LIMIT = 10;

export interface RecentDeploymentItem {
  id: number;
  projectId: string;
  status: DeploymentResult['status'];
  version: string;
  branch: string;
  deployedAt: string;
  description: string;
  /** 배포를 실행한 사용자 식별자 (로그인 ID). 비어있을 수 있다. */
  deployedBy: string;
  /** 어떤 서비스(앱) 가 배포됐는지 — site-batch deploy 만 채워짐. */
  appname?: string;
  /** 어떤 사이트로 배포됐는지 (banpo/osong/central). 표시용. */
  site?: string;
  /**
   * 배포 환경 태그 — 로컬/원격 분류 기준. 'company' → 로컬, 그 외 → 원격.
   * site-batch 는 운영자가 고른 토글값(company/remote), legacy 는 'production' 등.
   */
  environment?: string;
  /** GitHub Actions workflow_run id. 실패 행에서 GH Actions 링크 생성에 사용. */
  githubRunId?: number | null;
  /** 실패 사유 텍스트 — tooltip / 상세 dialog 에 노출. */
  failureReason?: string;
}

export const recentDeploymentsQueryKey = (projectId: string | null, limit: number) =>
  ['dashboard', 'recent-deployments', projectId, limit] as const;

interface UseRecentDeploymentsOptions {
  enabled: boolean;
  limit?: number;
}

/**
 * 대시보드 Timeline 위젯용 — 최근 완료된 배포 N건을 30초 간격 polling.
 *
 * `useActiveBatches` 와 동일한 패턴: `projectId` 는 backend path 요구사항이지만
 * list 자체는 cross-site 집계이므로 임의 유효 id 하나로 충분하다.
 *
 * 응답은 `deployed_at` 내림차순으로 정렬되어 반환된다고 가정하고,
 * 그대로 최근순 배열로 노출.
 */
export function useRecentDeployments(
  projectId: string | null,
  options: UseRecentDeploymentsOptions,
): UseQueryResult<RecentDeploymentItem[], Error> {
  const { enabled, limit = DEFAULT_LIMIT } = options;

  return useQuery<RecentDeploymentItem[], Error>({
    queryKey: recentDeploymentsQueryKey(projectId, limit),
    queryFn: async () => {
      const res = await deploymentService.getDeployments(projectId as string);
      const data = (res?.data ?? []).slice(0, limit);
      return data.map((d) => ({
        id: d.id,
        projectId: d.project_id,
        status: d.status,
        version: d.version,
        branch: d.branch,
        deployedAt: d.deployed_at,
        description: d.description,
        deployedBy: d.deployed_by ?? '',
        appname: d.appname,
        site: d.site,
        environment: d.environment,
        githubRunId: d.github_run_id ?? null,
        failureReason: d.failure_reason ?? '',
      }));
    },
    enabled: enabled && !!projectId,
    refetchInterval: () => {
      if (typeof document !== 'undefined' && document.hidden) return false;
      return POLLING_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    retry: 2,
    staleTime: 10_000,
  });
}
